/* Theona surprise gift (like Glphs): keeps exactly one €0 gift line in the cart.
   2 paid items -> gift worth €10, 3+ paid items -> gift worth €25.
   - one /cart/update.js call (with the cart sections) per correction, keyed by LINE KEY for existing gift lines
   - every cart change marks the cart busy (html.theona-cart-busy) until the gift is reconciled
   - checkout waits for Horizon's debounced/in-flight quantity changes AND the gift reconcile */
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const V10 = 49848661803267, V25 = 49848663998723;
const GIFTS = new Set([V10, V25]);
const NET_TIMEOUT = 8000;

let chain = Promise.resolve();
let queued = null;
let busy = 0;
let lastQtyInput = 0;

const comps = () => [...document.querySelectorAll('cart-items-component[data-section-id]')];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const busyOn = () => { busy += 1; document.documentElement.classList.add('theona-cart-busy'); };
const busyOff = () => { busy = Math.max(0, busy - 1); if (!busy) document.documentElement.classList.remove('theona-cart-busy'); };

async function getJSON(url, init = {}) {
  const signal = typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(NET_TIMEOUT) : undefined;
  const res = await fetch(url, { ...init, signal });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

/** Which gift should be in the cart, and the updates (line keys / variant ids) that get it there. */
function plan(items) {
  let n = 0;
  const giftLines = [];
  for (const it of items) {
    if (GIFTS.has(it.variant_id)) giftLines.push(it);
    else n += it.quantity;
  }
  const want = n >= 3 ? V25 : n === 2 ? V10 : null;
  const updates = {};
  let kept = false;
  for (const line of giftLines) {
    if (line.variant_id === want && !kept) {
      kept = true;
      if (line.quantity !== 1) updates[line.key] = 1;
    } else {
      updates[line.key] = 0; // wrong gift, duplicate gift line, or gift without enough paid items
    }
  }
  if (want && !kept) updates[want] = 1;
  return { updates, want };
}

async function reconcile() {
  const { res: r0, data: cart } = await getJSON(`${Theme.routes.cart_url}.js`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!r0.ok || !Array.isArray(cart.items)) throw new Error(`cart.js ${r0.status}`);

  const { updates, want } = plan(cart.items);
  if (!Object.keys(updates).length) {
    // Cart is right, but a late Horizon morph (e.g. 2nd rapid ATC) may have painted an older cart: repaint.
    const keys = cart.items.map((i) => i.key).join('|');
    for (const c of comps()) {
      const dom = [...c.querySelectorAll('tr[data-key]')].map((r) => r.dataset.key).join('|');
      if (dom !== keys) {
        sectionRenderer
          .renderSection(c.dataset.sectionId, { cache: false, mode: c.hasAttribute('data-drawer') ? 'hydration' : 'full' })
          .catch(() => {});
      }
    }
    return;
  }
  busyOn();
  try {

  const cs = comps();
  const ids = cs.map((c) => c.dataset.sectionId);
  cs.forEach((c) => sectionRenderer.abortRender(c.dataset.sectionId));

  const post = (u) => {
    const body = { updates: u };
    if (ids.length) { body.sections = ids.join(','); body.sections_url = location.pathname; }
    return getJSON(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  };

  let { res, data } = await post(updates);
  if (!res.ok && want && updates[want] === 1) {
    // Gift variant can't be added (draft / not on Online Store / deleted -> 404 "Cannot find variant").
    // Still remove the wrong gift lines instead of leaving the old gift in place.
    console.warn('[theona-gift] gift variant not addable', want, data);
    delete updates[want];
    if (!Object.keys(updates).length) return;
    ({ res, data } = await post(updates));
  }
  if (!res.ok) { console.warn('[theona-gift]', data); return; }

  for (const c of cs) {
    if (!c.isConnected) continue;
    const id = c.dataset.sectionId;
    const mode = c.hasAttribute('data-drawer') ? 'hydration' : 'full';
    const html = data.sections && data.sections[id];
    try {
      if (html) await morphSection(id, html, { mode });
      else await sectionRenderer.renderSection(id, { cache: false, mode });
    } catch (e) {
      console.warn('[theona-gift] render', id, e);
    }
  }
  document.querySelectorAll('cart-quantity-selector-component').forEach((s) => s.updateButtonStates?.());
  document.querySelectorAll('cart-icon').forEach((el) => el.renderCartBubble?.(data.item_count, false));
  document.dispatchEvent(new CustomEvent('theona:cart-synced', { detail: { itemCount: data.item_count } }));
  } finally {
    busyOff();
  }
}

/** Coalescing queue: the returned promise settles after a reconcile that STARTED after this call. */
export function syncTheonaGift() {
  if (queued) return queued;
  const next = chain
    .then(() => { queued = null; return reconcile(); })
    .catch((e) => console.warn('[theona-gift]', e));
  queued = next;
  chain = next;
  return next;
}

document.addEventListener('shopify:cart:lines-update', (e) => {
  busyOn();
  Promise.race([Promise.resolve(e.promise), sleep(15000)])
    .catch(() => {})
    .then(() => sleep(0))
    .then(syncTheonaGift)
    .finally(busyOff);
});
document.addEventListener('quantity-selector:update', () => { lastQtyInput = Date.now(); });
window.addEventListener('pageshow', () => { syncTheonaGift(); });

/** Wait for Horizon's 300 ms quantity debounce, its in-flight /cart/change calls and our own reconcile. */
async function settleCart() {
  const debounceLeft = lastQtyInput + 450 - Date.now();
  if (debounceLeft > 0) await sleep(debounceLeft);
  const mod = await import('@theme/component-cart-items').catch(() => null);
  for (let i = 0; i < 20; i++) {
    const pending = mod?.CartItemsComponent?.pendingQuantityUpdates ?? [];
    if (!pending.length && !busy) break;
    await Promise.race([Promise.allSettled(pending), sleep(250)]);
  }
  await syncTheonaGift();
}

/* Hold checkout until the gift line is correct (never longer than ~10 s) */
document.addEventListener('click', (e) => {
  const b = e.target.closest && e.target.closest('button[name="checkout"]');
  if (!b || b.dataset.theonaOk) return;
  e.preventDefault(); e.stopImmediatePropagation();
  if (b.getAttribute('aria-busy') === 'true') return; // already holding
  b.setAttribute('aria-busy', 'true');
  Promise.race([settleCart(), sleep(10000)]).finally(() => {
    b.removeAttribute('aria-busy');
    if (!b.isConnected || b.disabled) return;
    b.dataset.theonaOk = '1';
    b.click();
    delete b.dataset.theonaOk;
  });
}, true);
